import React, { useCallback, useEffect, useState } from "react";
import "../../App.css";
import "../../profile.css";
import { ArrowLeft, Pencil, Trash2, RotateCcw, X, Plus } from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import endpoints from "../../services/endpoints";
import service from "../../services/service";
import {
  GoogleMap,
  Polyline,
  Marker,
  OverlayView,
  useJsApiLoader,
} from "@react-google-maps/api";
import polyline from "@mapbox/polyline";

const decodePolyline = (encoded) => {
  return polyline.decode(encoded).map(([lat, lng]) => ({
    lat,
    lng,
  }));
};

const TeamDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  // Navigation source flags
  const isFromAllTeams = location.state?.isFromAllTeams || false;
  const isFromJoinedTeams = location.state?.isFromJoinedTeams || false;
  const isFromCreatedTeams = location.state?.isFromCreatedTeams || false;

  const [teamdetail, setTeamdetail] = useState(null);
  const [showStartSuccessModal, setShowStartSuccessModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);

  // Join Request State & Modal State
  const [joinRequestLoading, setJoinRequestLoading] = useState(false);
  const [showJoinRequestSuccessModal, setShowJoinRequestSuccessModal] =
    useState(false);

  // Invite Modal States
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [emails, setEmails] = useState([""]);
  const [inviteLoading, setInviteLoading] = useState(false);

  const [activeChallengeName, setActiveChallengeName] = useState("");
  const [loadingAction, setLoadingAction] = useState(false);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const fetchTeamdetail = useCallback(async () => {
    try {
      const res = await service.get(
        endpoints.teammanagment.teamdetail(id),
        {},
        { skipToast: true },
      );
      setTeamdetail(res.data.data);
    } catch (error) {
      console.log(error);
    }
  }, [id]);

  useEffect(() => {
    fetchTeamdetail();
  }, [fetchTeamdetail]);

  const routeCoords = teamdetail?.routePolyline
    ? decodePolyline(teamdetail.routePolyline)
    : [];

  const mapCenter =
    routeCoords.length > 0
      ? routeCoords[0]
      : {
          lat: teamdetail?.startPointLatitude || 28.6139,
          lng: teamdetail?.startPointLongitude || 77.209,
        };

  const onMapLoad = useCallback(
    (map) => {
      if (routeCoords.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        routeCoords.forEach((point) => bounds.extend(point));
        map.fitBounds(bounds);
      }
    },
    [routeCoords],
  );

  if (!teamdetail) return <div>Loading...</div>;

  const hours = Math.floor((teamdetail.durationInMin || 0) / 60);
  const minutes = (teamdetail.durationInMin || 0) % 60;

  // Benefits / Value Props List
  const teamBenefits = [
    {
      icon: "⚡",
      title: "Shared Progress",
      description:
        "Pool distances together to conquer long-distance routes faster.",
    },
    {
      icon: "🔥",
      title: "Daily Task Streaks",
      description: "Keep each other accountable with team streak tracking.",
    },
    {
      icon: "🥇",
      title: "Squad Leaderboard",
      description:
        "Compete against other teams globally or in private leagues.",
    },
  ];

  // Handle Join Request API Integration
  const handleSendJoinRequest = async () => {
    try {
      setJoinRequestLoading(true);

      const payload = {
        teamId: Number(teamdetail?.id || id),
      };

      const res = await service.post(
        endpoints.teammanagment.joinRequest,
        payload,
      );
      if (res?.status === 200 || res?.data) {
        setShowJoinRequestSuccessModal(true);
      }
    } catch (error) {
      console.log("Error sending join request:", error);
    } finally {
      setJoinRequestLoading(false);
    }
  };

  const handleStartChallenge = async () => {
    try {
      setLoadingAction(true);
      await service.patch(
        endpoints.profile.startChallenge(teamdetail.challengeId),
      );
      setShowStartSuccessModal(true);
    } catch (error) {
      console.log(error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        "";

      if (
        message.toLowerCase().includes("already") ||
        message.toLowerCase().includes("progress")
      ) {
        setActiveChallengeName(
          error?.response?.data?.data?.challengeName || "Active Challenge",
        );
        setShowPauseModal(true);
      }
    } finally {
      setLoadingAction(false);
    }
  };

  const handlePauseCurrent = async () => {
    try {
      setLoadingAction(true);
      await service.patch(endpoints.profile.pauseCurrent);
      setShowPauseModal(false);
      await service.patch(
        endpoints.profile.startChallenge(teamdetail.challengeId),
      );
      setShowStartSuccessModal(true);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingAction(false);
    }
  };

  // Invite Modal Actions
  const handleEmailChange = (index, value) => {
    const updatedEmails = [...emails];
    updatedEmails[index] = value;
    setEmails(updatedEmails);
  };

  const addEmailField = () => {
    setEmails([...emails, ""]);
  };

  const removeEmailField = (index) => {
    const updatedEmails = emails.filter((_, i) => i !== index);
    setEmails(updatedEmails.length ? updatedEmails : [""]);
  };

  const handleSendInvitations = async (e) => {
    e.preventDefault();
    const filteredEmails = emails.filter((email) => email.trim() !== "");
    if (filteredEmails.length === 0) return;

    try {
      setInviteLoading(true);
      const payload = {
        teamId: teamdetail.id,
        sendToAll: false,
        emails: filteredEmails,
      };

      await service.post(endpoints.teammanagment.sendInvite, payload);

      setShowInviteModal(false);
      setEmails([""]);
    } catch (error) {
      console.log("Error sending dynamic invites:", error);
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div className="team-details-page">
      {/* HEADER */}
      <div className="details-header">
        <div className="details-left">
          <ArrowLeft className="cursor" onClick={() => navigate(-1)} />
          <h2>Team Details</h2>
        </div>

        {/* SHOW EDIT/DELETE ONLY FOR TEAMS CREATED BY ME */}
        {isFromCreatedTeams && (
          <div className="details-actions">
            <button className="team-icon-btn">
              <Trash2 size={16} />
            </button>
            <button
              className="team-icon-btn"
              onClick={() => navigate(`/team-management/edit-group/${id}`)}
            >
              <Pencil size={16} />
            </button>
          </div>
        )}
      </div>

      {/* TOP CARD */}
      <div className="team-info-card">
        <div className="team-card-left">
          <div className="team-logo">
            {teamdetail.teamName?.charAt(0).toUpperCase()}
          </div>
          <div>
            <h3>{teamdetail.teamName}</h3>
            <p>{teamdetail.memberCount} Member</p>
          </div>
          <div style={{ marginLeft: "50px" }}>
            <h6>
              {teamdetail.challenge_startdate
                ? new Date(teamdetail.challenge_startdate).toLocaleDateString(
                    "en-GB",
                    {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    },
                  )
                : "—"}
            </h6>
            <p>
              {teamdetail.challenge_starttime
                ? new Date(teamdetail.challenge_starttime).toLocaleTimeString(
                    "en-US",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    },
                  )
                : "—"}
            </p>
          </div>
        </div>

        {/* CONDITIONALLY RENDER BUTTONS ACCORDING TO ACTIVE TAB */}
        <div className="team-btns">
          {/* 1. All Teams -> Send Join Request */}
          {isFromAllTeams && (
            <button
              className="red-btn"
              onClick={handleSendJoinRequest}
              disabled={joinRequestLoading}
            >
              {joinRequestLoading ? "Sending Request..." : "Send Join Request"}
            </button>
          )}

          {isFromJoinedTeams && (
            <button
              className="red-btn"
              onClick={handleStartChallenge}
              disabled={loadingAction}
            >
              {loadingAction ? "Please wait..." : "Start Challenge"}
            </button>
          )}

          {isFromCreatedTeams && (
            <>
              <button
                className="outline-btn"
                onClick={() => setShowInviteModal(true)}
              >
                Invite Member
              </button>
              <button
                className="red-btn"
                onClick={handleStartChallenge}
                disabled={loadingAction}
              >
                {loadingAction ? "Please wait..." : "Start Challenge"}
              </button>
            </>
          )}
        </div>
      </div>

      <div
        className="map-box"
        style={{ height: "350px", borderRadius: "12px", overflow: "hidden" }}
      >
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "100%",
            }}
            center={mapCenter}
            zoom={7}
            onLoad={onMapLoad}
            options={{
              streetViewControl: true,
              mapTypeControl: true,
              fullscreenControl: true,
            }}
          >
            {routeCoords.length > 1 && (
              <Polyline
                path={routeCoords}
                options={{
                  strokeColor: "#ff4444",
                  strokeWeight: 5,
                }}
              />
            )}

            <Marker
              position={{
                lat: teamdetail.startPointLatitude,
                lng: teamdetail.startPointLongitude,
              }}
            />

            <OverlayView
              position={{
                lat: teamdetail.startPointLatitude,
                lng: teamdetail.startPointLongitude,
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  transform: "translate(-50%, -65px)",
                  background: "#fff",
                  border: "2px solid #ef4444",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#222",
                  boxShadow: "0 3px 10px rgba(0,0,0,.25)",
                  width: "100px",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {teamdetail.startPoint}
              </div>
            </OverlayView>

            {teamdetail.milestoneDetails?.map((milestone) => (
              <React.Fragment key={milestone.id}>
                <Marker
                  position={{
                    lat: milestone.latitude,
                    lng: milestone.longitude,
                  }}
                />

                <OverlayView
                  position={{
                    lat: milestone.latitude,
                    lng: milestone.longitude,
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    style={{
                      transform: "translate(-50%, -65px)",
                      background: "#fff",
                      border: "2px solid #ef4444",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#222",
                      boxShadow: "0 3px 10px rgba(0,0,0,.25)",
                      width: "100px",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {milestone.title}
                  </div>
                </OverlayView>
              </React.Fragment>
            ))}

            <Marker
              position={{
                lat: teamdetail.endPointLatitude,
                lng: teamdetail.endPointLongitude,
              }}
            />

            <OverlayView
              position={{
                lat: teamdetail.endPointLatitude,
                lng: teamdetail.endPointLongitude,
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  transform: "translate(-50%, -65px)",
                  background: "#fff",
                  border: "2px solid #ef4444",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#222",
                  boxShadow: "0 3px 10px rgba(0,0,0,.25)",
                  width: "100px",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {teamdetail.endPoint}
              </div>
            </OverlayView>
          </GoogleMap>
        )}
      </div>

      {/* STATS */}
      <div className="stats-row">
        <div>
          <span>Distance</span>
          <h4>{teamdetail.totalDistance} KM</h4>
        </div>
        <div>
          <span>Milestones</span>
          <h4>{teamdetail.totalMilestoneCount}</h4>
        </div>
        <div>
          <span>Duration</span>
          <h4>
            {hours}H {minutes}M
          </h4>
        </div>
        <div>
          <span>Challenge Type</span>
          <h4>{teamdetail.challengeType}</h4>
        </div>
      </div>

      {/* OVERVIEW */}
      <div className="team-overview-layout">
        <div className="team-overview-left">
          <div className="team-section">
            <h3>Mission Overview</h3>
            <p>{teamdetail.challengeOverview}</p>
          </div>

          <div className="team-section">
            <h3>Rewards</h3>
            <div className="team-reward-grid">
              {[1, 2, 3].map((item) => (
                <div className="team-reward-card" key={item}>
                  <span className="team-flag">🚩</span>
                  <h4>{teamdetail.rewardPoints}</h4>
                  <p>
                    {item === 1
                      ? "RACE BIB"
                      : item === 2
                        ? "POSTCARDS"
                        : "FINISHER CERTIFICATE"}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* UPDATED BENEFITS SECTION */}
          <div className="team-section">
            <h3>Why Challenge As A Team?</h3>
            <div className="team-benefit-grid">
              {teamBenefits.map((benefit, index) => (
                <div className="team-reward-card" key={index}>
                  <span className="team-flag" style={{ fontSize: "20px" }}>
                    {benefit.icon}
                  </span>
                  <h3>{benefit.title}</h3>
                  <p
                    style={{
                      textTransform: "none",
                      opacity: 0.85,
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* TEAM MEMBERS */}
        <div className="team-members-box">
          <h3>Team Members ({teamdetail.memberCount})</h3>

          <div className="team-member-row team-member-heading">
            <span>MEMBER NAME</span>
            <span>Action</span>
          </div>

          {teamdetail.teamMembers?.map((member) => (
            <div className="team-member-row" key={member.userId}>
              <span>{member.name}</span>
              <span>
                {member.role === "CAPTAIN" ? (
                  "Captain"
                ) : (
                  <RotateCcw size={16} color="#ff0000" />
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* DYNAMIC INVITE MODAL */}
      {showInviteModal && (
        <div className="challenge-modal-overlay">
          <div
            className="challenge-success-modal"
            style={{ maxWidth: "450px", width: "100%" }}
          >
            <button
              className="challenge-modal-close"
              onClick={() => {
                setShowInviteModal(false);
                setEmails([""]);
              }}
            >
              <X size={18} />
            </button>

            <div className="challenge-success-icon">✉️</div>
            <h2>Invite Team Members</h2>
            <p style={{ marginBottom: "20px" }}>
              Send an email invitation link to your partners.
            </p>

            <form
              onSubmit={handleSendInvitations}
              style={{ width: "100%", textAlign: "left" }}
            >
              <div
                style={{
                  maxHeight: "180px",
                  overflowY: "auto",
                  paddingRight: "5px",
                  marginBottom: "15px",
                }}
              >
                {emails.map((email, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginBottom: "10px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="email"
                      placeholder="Enter user email address"
                      value={email}
                      required
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                        fontSize: "14px",
                      }}
                    />
                    {emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmailField(index)}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#ef4444",
                          cursor: "pointer",
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addEmailField}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  background: "none",
                  border: "none",
                  color: "#ff4444",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  marginBottom: "20px",
                }}
              >
                <Plus size={16} /> Add another email
              </button>

              <button
                type="submit"
                className="challenge-success-btn"
                disabled={inviteLoading}
                style={{ width: "100%" }}
              >
                {inviteLoading ? "Sending Invites..." : "Send Invitations"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* JOIN REQUEST SUCCESS MODAL */}
      {showJoinRequestSuccessModal && (
        <div className="challenge-modal-overlay">
          <div className="challenge-success-modal">
            <button
              className="challenge-modal-close"
              onClick={() => setShowJoinRequestSuccessModal(false)}
            >
              ✕
            </button>
            <div className="challenge-success-icon">📩</div>
            <h2>Request Sent!</h2>
            <p>
              Your join request has been sent to the captain of{" "}
              <strong>{teamdetail?.challengeName || "the challenge"}</strong>.
            </p>
            <div className="challenge-name-pill">
              🏆 {teamdetail?.teamName || "Team"}
            </div>
            <button
              className="challenge-success-btn"
              onClick={() => setShowJoinRequestSuccessModal(false)}
            >
              ✓ Okay, thank you!
            </button>
          </div>
        </div>
      )}

      {showStartSuccessModal && (
        <div className="challenge-modal-overlay">
          <div className="challenge-success-modal">
            <button
              className="challenge-modal-close"
              onClick={() => setShowStartSuccessModal(false)}
            >
              ✕
            </button>
            <div className="challenge-success-icon">🚀</div>
            <h2>Challenge started!</h2>
            <p>Stay strong, stay focused — every step counts.</p>
            <div className="challenge-name-pill">
              🏆 {teamdetail.challengeName}
            </div>
            <button
              className="challenge-success-btn"
              onClick={() => setShowStartSuccessModal(false)}
            >
              ✓ Okay, thank you!
            </button>
          </div>
        </div>
      )}

      {showPauseModal && (
        <div className="challenge-modal-overlay">
          <div className="challenge-pause-modal">
            <div className="warning-circle">⚠</div>
            <h3>
              One challenge is already in progress. Please pause it first.
            </h3>
            <div className="active-challenge-pill">
              🏆 {activeChallengeName}
            </div>
            <div className="challenge-modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowPauseModal(false)}
              >
                Cancel
              </button>
              <button className="pause-btn" onClick={handlePauseCurrent}>
                Pause active
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDetails;
